import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X, Camera, Upload, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

interface AddExtraFormProps {
  eventData: {
    id: string;
    extra?: number;
    costs_1?: number;
    costs_2?: number;
    costs_1_file?: string; // URL publique Firebase
    costs_2_file?: string; // URL publique Firebase
  };
  onSubmit: (extra1: number, extra2: number, file1: File | null, file2: File | null) => void | Promise<void>;
  onDeleteFile: (fileField: 'costs_1_file' | 'costs_2_file') => Promise<void>;
  onCancel: () => void;
}

interface UploadZoneProps {
  fileUrl: string | null; // URL publique Firebase
  file: File | null;     // Nouveau fichier uploadé
  onChange: (file: File | null) => void;
  onDelete?: () => void;
  id: string;
  label: string;
}

const UploadZone: React.FC<UploadZoneProps> = ({ fileUrl, file, onChange, onDelete, id, label }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { t } = useLanguage();

  const handleFile = (f: File) => {
    onChange(f);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) handleFile(e.target.files[0]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (file) {
      onChange(null);
    } else if (fileUrl && onDelete) {
      onDelete();
    }
  };

  // Affiche soit le nouveau fichier uploadé, soit l'URL Firebase existante
  const hasContent = file || fileUrl;
  const previewUrl = file ? URL.createObjectURL(file) : fileUrl;

  return (
    <div className="space-y-2">
      <div
        className={cn(
          'relative rounded-lg border-2 border-dashed transition-all duration-150 cursor-pointer overflow-hidden',
          isDragging && 'border-blue-400 bg-blue-50',
          hasContent && !isDragging && 'border-solid border-emerald-400 bg-emerald-50',
          !hasContent && !isDragging && 'border-border bg-muted/40 hover:border-blue-300 hover:bg-blue-50/40'
        )}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleChange}
        />

        {previewUrl ? (
          <div className="relative">
            <img
              src={previewUrl}
              alt={label}
              className="w-full h-32 object-cover"
            />
            <div className="absolute inset-0 bg-black/10" />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-6 px-4 gap-1.5 select-none">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Camera className="w-5 h-5" />
              <Upload className="w-4 h-4" />
            </div>
            <p className="text-sm text-muted-foreground font-medium">
              {t('agenda.addExtraForm.takePhotoOrUpload')}
            </p>
            <p className="text-xs text-muted-foreground/70">JPG, PNG — max 10 Mo</p>
          </div>
        )}
      </div>

      {hasContent && (
        <div className="flex items-center justify-between px-1">
          {/*<span className="text-xs text-emerald-700 font-medium truncate max-w-[80%]">
            {file ? file.name : 'Fichier existant'}
          </span>*/}
          <button
            type="button"
            onClick={handleRemove}
            className="text-xs text-destructive hover:underline flex items-center gap-1 shrink-0"
          >
            <X className="w-3 h-3" /> {t('common.delete')}
          </button>
        </div>
      )}
    </div>
  );
};

const AddExtraForm: React.FC<AddExtraFormProps> = ({ eventData, onSubmit, onDeleteFile, onCancel }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [extra1] = useState<number>(eventData.costs_1 ?? eventData.extra ?? 0);
  const [extra2, setExtra2] = useState<number>(eventData.costs_2 ?? 0);
  const [file1, setFile1] = useState<File | null>(null);
  const [file2, setFile2] = useState<File | null>(null);

  const { toast } = useToast();
  const { t } = useLanguage();

  const handleSubmit = async () => {
    if (extra2 < 0) {
      toast({
        title: t('common.invalidAmount'),
        description: t('agenda.addExtraForm.negativeAmountError'),
        variant: 'destructive',
      });
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit(extra1, extra2, file1, file2);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col max-h-[92vh] lg:max-h-[60vh]">
      
      {/* HEADER (fixe) */}
      <div className="p-4 lg:p-1 pb-4 border-b">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-base font-semibold leading-tight">
              {t('agenda.addExtraForm.title')}
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {t('agenda.addExtraForm.subTitle')}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onCancel} className="-mr-1 -mt-1">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* CONTENU SCROLLABLE */}
      <div className="flex-1 overflow-y-auto px-2 py-5 space-y-5">
        
        {/* Section frais scolaires */}
        <div className="rounded-xl border bg-card p-4 space-y-3 bg-gray-50">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('agenda.addExtraForm.schoolFees')}
          </Label>

          <div className="flex items-center gap-2 pb-3 border-b">
            <span className="text-muted-foreground text-sm">€</span>
            <Input type="number" value={extra1} readOnly className='cursor-not-allowed' />
            <span className="text-xs text-muted-foreground bg-muted rounded px-2 py-0.5 shrink-0">
              {t('agenda.addExtraForm.preFilled')}
            </span>
          </div>

          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">{t('agenda.addExtraForm.receipt')}</p>
            <UploadZone
              fileUrl={eventData.costs_1_file || null}
              file={file1}
              onChange={setFile1}
              onDelete={() => onDeleteFile('costs_1_file')}
              id="upload-school"
              label="Ticket école"
            />
          </div>
        </div>

        {/* Section frais enseignant */}
        <div className="rounded-xl border bg-card p-4 space-y-3 bg-gray-50">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('agenda.addExtraForm.teacherFees')}
          </Label>

          <div className="flex items-center gap-2 pb-3 border-b">
            <span className="text-muted-foreground text-sm">€</span>
            <Input
              type="number"
              value={extra2 || ''}
              onChange={(e) => setExtra2(parseFloat(e.target.value) || 0)}
              placeholder="0.00"
              min={0}
              step={0.01}
            />
          </div>

          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">{t('agenda.addExtraForm.receipt')}</p>
            <UploadZone
              fileUrl={eventData.costs_2_file || null}
              file={file2}
              onChange={setFile2}
              onDelete={() => onDeleteFile('costs_2_file')}
              id="upload-teacher"
              label="Ticket enseignant"
            />
          </div>
        </div>

      </div>

      {/* FOOTER FIXE */}
      <div className="border-t bg-background p-4 flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('common.processing')}
            </span>
          ) : (
            t('common.done')
          )}
        </Button>
      </div>

    </div>
  );
};

export default AddExtraForm;